import moment from 'moment';
import { DmpHlGetDirectAuthentificationDmpStatus } from 'src/app/utils/interfaces/new/dmp-hl-get-direct-authentification-dmp-status.interface';
import { DmpHlFindDocumentsResponse } from 'src/app/utils/interfaces/new/dmp-hl-find-documents-response.interface';
import { DmpParameters } from '../dmp-service.abstract';

/**
 * Maps the raw REST response from /getDmpStatus to the DmpHlGetDirectAuthentificationDmpStatus interface expected by the application.
 * 
 * @param restResponse The raw JSON response returned by the REST API.
 * @returns Mapped object of type DmpHlGetDirectAuthentificationDmpStatus.
 */
export function mapRestToDmpStatus(restResponse: any): DmpHlGetDirectAuthentificationDmpStatus {
  if (!restResponse) {
    return undefined;
  }

  const adminData = restResponse.AdminData || {};
  const ins = adminData.Ins || {};

  return {
    ExistingTestAnswer: {
      AdminData: {
        Ins: {
          s_ins: ins.s_ins || '',
          s_insType: ins.s_insType || 'T'
        },
        s_birthday: adminData.s_birthday && adminData.s_birthday !== '' ? moment(adminData.s_birthday, 'YYYYMMDD').toDate() : undefined,
        s_insC: adminData.s_insC || '',
        s_patientGivenName: adminData.s_patientGivenName || '',
        s_patientName: adminData.s_patientName || '',
        s_patientBirthName: adminData.s_patientBirthName || '',
        s_patientFirstBirthGiven: adminData.s_patientFirstBirthGiven || ''
      },
      i_dmpStatus: restResponse.i_dmpStatus,
      i_sex: restResponse.i_sex,
      i_userAuthorization: restResponse.i_userAuthorization,
      i_webAccessIsOpen: Boolean(restResponse.i_webAccessIsOpen),
      i_ensRelatedDmp: Boolean(restResponse.i_ensRelatedDmp),
      s_closingDate: restResponse.s_closingDate && restResponse.s_closingDate !== '' ? moment(restResponse.s_closingDate, 'YYYYMMDD').toDate() : undefined,
      s_closingMessage: restResponse.s_closingMessage || ''
    },
    i_treatingPhysician: Boolean(restResponse.i_treatingPhysician),
    s_mail: restResponse.s_mail || '',
    s_mobilePhone: restResponse.s_mobilePhone || '',
    s_status: restResponse.s_status || ''
  };
}

/**
 * Helper to parse various string date formats returned by REST API into native Date objects
 */
function parseRestDate(dateStr: string): Date | undefined {
  if (!dateStr || dateStr === '') {
    return undefined;
  }
  // Try YYYYMMDDHHmmss format
  let m = moment(dateStr, 'YYYYMMDDHHmmss');
  if (m.isValid()) {
    return m.toDate();
  }
  // Fallback to YYYYMMDD format
  m = moment(dateStr, 'YYYYMMDD');
  if (m.isValid()) {
    return m.toDate();
  }
  return undefined;
}

/**
 * Maps the raw REST response from /findDocuments to an array of DmpHlFindDocumentsResponse expected by the application.
 * 
 * @param restResponse The raw JSON response from /findDocuments REST API.
 * @returns Array of mapped DmpHlFindDocumentsResponse.
 */
export function mapRestToFindDocuments(restResponse: any): Array<DmpHlFindDocumentsResponse> {
  if (!restResponse || !Array.isArray(restResponse.Documents)) {
    return [];
  }

  return restResponse.Documents.map((doc: any) => {
    return {
      s_uniqueId: doc.s_uniqueId || '',
      s_title: doc.s_title || '',
      s_description: doc.s_description || '',
      s_classCode: doc.s_classCode || '',
      s_typeCode: doc.s_typeCode || '',
      s_creationDate: parseRestDate(doc.s_creationDate),
      s_serviceStartDate: parseRestDate(doc.s_serviceStartDate),
      s_serviceStopDate: parseRestDate(doc.s_serviceStopDate),
      s_healthCareFacilityTypeCode: doc.s_healthCareFacilityTypeCode || '',
      s_practiceSettingCode: doc.s_practiceSettingCode || '',
      s_submissionDate: parseRestDate(doc.s_submissionDate),
      s_uuid: doc.s_uuid || '',
      // Map REST field s_previousUUId and s_nextUUId to interface camelCase uuid equivalents
      s_previousUuid: doc.s_previousUuid || doc.s_previousUUId || '',
      s_nextUuid: doc.s_nextUuid || doc.s_nextUUId || '',
      documentId: doc.documentId,
      i_document_Status: doc.i_document_Status,
      i_document_Visibility: doc.i_document_Visibility,
      i_document_Format: doc.i_document_Format,
      Authors: Array.isArray(doc.Authors) ? doc.Authors.map((author: any) => ({
        s_hpName: author.s_hpName || '',
        s_hpGiven: author.s_hpGiven || '',
        s_hpProfession: author.s_hpProfession || '',
        s_hpProfessionOid: author.s_hpProfessionOid || '',
        s_hpProfessionDescription: author.s_hpProfessionDescription || '',
        s_hpSpeciality: author.s_hpSpeciality || '',
        s_hpSpecialityDescription: author.s_hpSpecialityDescription || '',
        s_hpInternalId: author.s_hpInternalId || '',
        s_hpInstitution: author.s_hpInstitution || ''
      })) : [],
      EventCodes: Array.isArray(doc.EventCodes) ? doc.EventCodes : [],
      i_handle: doc.i_handle,
      s_documentFormatCode: doc.s_documentFormatCode || '',
      s_documentFormatDescription: doc.s_documentFormatDescription || '',
      s_submissionSetId: doc.s_submissionSetId || ''
    };
  });
}

/**
 * Maps the raw REST response from /getCisCodes to the format expected by the application,
 * matching the websocket strategy.
 * 
 * @param restResponse Raw REST response from /getCisCodes.
 * @returns Array of mapped CIS codes.
 */
export function mapRestToCisCodes(restResponse: any): Array<{ id: string; libelle: string; atcCodes?: string; ucdCodes?: any }> {
  if (!restResponse || !Array.isArray(restResponse.Codes)) {
    return [];
  }

  return restResponse.Codes.map((c: any) => {
    return {
      id: c.s_code,
      libelle: c.s_name,
      atcCodes: c.s_atcCode,
      ucdCodes: c.UcdCodes
    };
  });
}

/**
 * Maps the raw REST response from /getAtcCodes to the format expected by the application,
 * matching the websocket strategy (libellé français, à défaut libellé OMS).
 * Cf. Documentation_eSanteConnect-Proxy § 4.2.6.1 : chaque code = { s_code, Names: { s_fr, s_en }, i_level }.
 * La racine peut être { Codes: [...] } (comme /getCisCodes) ou directement le tableau.
 *
 * @param restResponse Raw REST response from /getAtcCodes.
 * @returns Array of mapped ATC codes.
 */
export function mapRestToAtcCodes(restResponse: any): Array<{ id: string; libelle: string }> {
  const codes = Array.isArray(restResponse) ? restResponse : restResponse?.Codes;
  if (!Array.isArray(codes)) {
    return [];
  }

  return codes.map((c: any) => {
    return {
      id: c.s_code,
      libelle: c.Names?.s_fr || c.Names?.s_en
    };
  });
}

/**
 * Maps the raw REST response from /getDmpParameters to DmpParameters, matching the websocket strategy.
 * Le format de la réponse REST n'est pas documenté (§ 5.6.1) : les champs préfixés (i_…) sont prioritaires,
 * les champs déjà non préfixés sont acceptés à défaut.
 *
 * @param restResponse Raw REST response from /getDmpParameters.
 * @returns Mapped DmpParameters, or undefined if the response is empty.
 */
export function mapRestToDmpParameters(restResponse: any): DmpParameters {
  if (!restResponse) {
    return undefined;
  }

  return {
    cumulMasquePsPatient: Boolean(restResponse.i_cumulMasquePsPatient ?? restResponse.cumulMasquePsPatient),
    hrMaxSearchPeriod: restResponse.i_hrMaxSearchPeriod ?? restResponse.hrMaxSearchPeriod,
    majorityAge: restResponse.i_majorityAge ?? restResponse.majorityAge,
    minorFunctionManagement: Boolean(restResponse.i_minorFunctionManagement ?? restResponse.minorFunctionManagement)
  };
}
